import React, { useState } from "react";
import { QrCode, Download, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@pgs-core/ui/components/card";
import { Button } from "@pgs-core/ui/components/button";
import { MenuModuleGuard, MenuModuleHeader } from "../components/menu-module-shell";
import { fetchMenuQr } from "../lib/menu-module";
import type { ClientOutletContext } from "../lib/outlet-context";

/**
 * The table QR code tab: generated on demand, previewed inline, downloadable
 * as an SVG. It points at the client's own /menu page — their own domain, not
 * a third-party QR service.
 */
function QrCodes({ api }: ClientOutletContext) {
  const [qr, setQr] = useState<{ svg: string; url: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    if (!api) return;
    setLoading(true);
    setError("");
    try {
      setQr(await fetchMenuQr(api));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate QR code");
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!qr) return;
    const blob = new Blob([qr.svg], { type: "image/svg+xml" });
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = "menu-qr-code.svg";
    link.click();
    URL.revokeObjectURL(href);
  };

  return (
    <div>
      <MenuModuleHeader />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <QrCode className="h-4 w-4 text-muted-foreground" />
            Table QR code
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 max-w-prose text-sm text-muted-foreground">
            Print this on tables, menus or windows — scanning it opens the menu page on
            your own website, so it always shows your latest published menu.
          </p>
          {qr ? (
            <div className="flex items-center gap-6">
              <img
                src={`data:image/svg+xml;utf8,${encodeURIComponent(qr.svg)}`}
                alt={`QR code linking to ${qr.url}`}
                className="h-40 w-40 rounded-md border bg-white p-1"
              />
              <div className="space-y-2">
                <p className="break-all text-xs text-muted-foreground">{qr.url}</p>
                <Button size="sm" variant="outline" onClick={download}>
                  <Download className="h-4 w-4" />
                  Download SVG
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" disabled={loading} onClick={generate}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
              Generate QR code
            </Button>
          )}
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>
    </div>
  );
}

export function MenuQrPage() {
  return (
    <MenuModuleGuard title="Menu" description="Your menu's table QR code.">
      {(ctx) => <QrCodes {...ctx} />}
    </MenuModuleGuard>
  );
}
